import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Skeleton, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import FactoryIcon from '@mui/icons-material/Factory';
import GroupIcon from '@mui/icons-material/Groups';
import GroupsIcon from '@mui/icons-material/Group';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';

import NodeStore from '../../../stores/node-store';
import { DbNode, NodeType } from '../../../types/node';
import { DeviceInfo, DeviceType } from '../../../types/device';
import { useDeviceDnd } from '../../../contexts/DeviceDndContext';
import { SensorType } from '../../../types/sensor';
import NodeView from './NodeView';
import DeviceView from '../device/DeviceView';
import { Selection } from '../selection';
import NodePositionStore from '../../../stores/node-position-store';

export function nodeIcon(type: NodeType) {
    // prettier-ignore
    switch (type) {
        case NodeType.Root:         return <LanguageIcon fontSize="small" />;
        case NodeType.Organization: return <FactoryIcon fontSize="small" />;
        case NodeType.Department:   return <GroupIcon fontSize="small" />;
        case NodeType.Group:        return <GroupsIcon fontSize="small" />;
        case NodeType.Borehole:     return <GolfCourseIcon fontSize="small" />;
    }
}

//=================================================================================================
export function NodeChildrenSkeleton() {
    return (
        <>
            {[60, 45].map((w) => (
                <Box key={w} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 0.75 }}>
                    <Skeleton variant="rectangular" width={12} height={12} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="circular" width={18} height={18} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="text" width={`${w}%`} sx={{ fontSize: 'body2.fontSize' }} />
                </Box>
            ))}
        </>
    );
}

export function NodeLoadError() {
    return (
        <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" color="error">
                Ошибка загрузки данных
            </Typography>
        </Box>
    );
}

//=================================================================================================
interface DeviceItemProps {
    device: DeviceInfo;
    depth: number;
    selection?: Selection;
    onSelect: (sel: Selection) => void;
}

export function DeviceItem({ device, depth, selection, onSelect }: DeviceItemProps) {
    const dnd = useDeviceDnd();
    const info = device.info ?? {};
    const view = (
        <DeviceView
            deviceId={device.device_id}
            deviceType={(info.deviceType as DeviceType) ?? DeviceType.Device_Unknown}
            sensorType={(info.sensorType as SensorType) ?? SensorType.UnknownSensorType}
            sensorId={info.sensorId ?? 0}
            lastTransaction={info.lastTransaction ?? new Date().toISOString()}
            logConfig={info.logConfig}
            nodeId={device.node_id}
            battery={info.battery}
            rssi={info.rssi}
            sensorState={info.sensorState}
            selected={selection?.kind === 'device' && selection.deviceId === device.device_id}
            depth={depth}
            onClick={(deviceId) => onSelect({ kind: 'device', deviceId: deviceId, nodeId: device.node_id })}
        />
    );
    if (!dnd) return view;
    return (
        <div
            draggable
            onDragStart={() => dnd.onDragStart(device)}
            onDragEnd={() => dnd.onDragEnd()}
            style={{ opacity: dnd.draggedDevice?.device_id === device.device_id ? 0.4 : 1, cursor: 'grab' }}
        >
            {view}
        </div>
    );
}

//=================================================================================================
interface NodeItemProps {
    node: DbNode;
    depth: number;
    selection?: Selection;
    onSelect: (sel: Selection) => void;
    expandedIds?: Set<number>;
    renderDevice?: (device: DeviceInfo, depth: number) => React.ReactNode;
}

const NodeItem = observer(({ node, depth, selection, onSelect, expandedIds, renderDevice }: NodeItemProps) => {
    const dnd = useDeviceDnd();
    const isPathNode = expandedIds?.has(node.node_id) ?? false;
    const [store] = useState(() => new NodeStore(node.node_id));
    const [lstore] = useState(() => new NodePositionStore(node.node_id));
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        store
            .load()
            .then(() => setLoaded(true))
            .catch(() => setError(true));
        lstore.load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshToken = dnd?.refreshToken;
    useEffect(() => {
        if (!loaded) return;
        store.load().catch(() => {});
    }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClick = async () => {
        lstore.location
            ? onSelect({ kind: 'location', nodeId: node.node_id, locationId: lstore.location.location_id })
            : onSelect({ kind: 'node', nodeId: node.node_id });
        if (loaded || store.loading) return;
        try {
            await store.load();
            setLoaded(true);
        } catch {
            setError(true);
        }
    };

    let content: React.ReactNode;
    if (!loaded && store.loading) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else if (loaded && store.nodes.length === 0 && store.devices.length === 0) {
        content = undefined; // leaf node — убираем стрелку раскрытия
    } else {
        content = (
            <>
                {store.nodes.map((n) => (
                    <NodeItem
                        key={n.node_id}
                        node={n}
                        depth={depth + 1}
                        selection={selection}
                        onSelect={onSelect}
                        expandedIds={expandedIds}
                        renderDevice={renderDevice}
                    />
                ))}
                {store.devices.map((d) =>
                    renderDevice ? (
                        <React.Fragment key={d.device_id}>{renderDevice(d, depth + 1)}</React.Fragment>
                    ) : (
                        <DeviceItem
                            key={d.device_id}
                            device={d}
                            depth={depth + 1}
                            selection={selection}
                            onSelect={onSelect}
                        />
                    ),
                )}
            </>
        );
    }

    return (
        <NodeView
            name={node.name}
            description={node.description ?? undefined}
            icon={nodeIcon(node.type)}
            depth={depth}
            defaultOpen={isPathNode}
            forceOpen={isPathNode}
            selected={
                selection != null &&
                (selection.kind === 'node' || selection.kind === 'location') &&
                selection.nodeId === node.node_id
            }
            onClick={handleClick}
            onDrop={dnd ? () => dnd.onDrop(node.node_id, node.name) : undefined}
        >
            {content}
        </NodeView>
    );
});

export default NodeItem;
